import { MAX_OPPORTUNITIES } from "./opportunitySelection";
import { WIZARD_HASHTAGS_ENABLED } from "../constants/campaignWizard";

export const MAX_FOLLOW_UP_HASHTAGS = 3;
export const MAX_TONES = 3;

export const TONE_OPTIONS = [
    "Friendly",
    "Professional",
    "Serious",
    "Humorous",
    "Informative",
];

function getEffectiveHashtags(values) {
    if (!WIZARD_HASHTAGS_ENABLED) return [];
    return values.hashtags ?? [];
}

export function normalizeToneList(tone) {
    if (Array.isArray(tone)) {
        return tone.map((item) => String(item).trim()).filter(Boolean);
    }
    if (typeof tone === "string" && tone.trim()) {
        return tone
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return [];
}

export function serializeTones(tone) {
    return normalizeToneList(tone).join(", ");
}

/** Keep only known pill options (case-insensitive), for form state. */
export function parseToneSelections(tone) {
    const byLower = Object.fromEntries(
        TONE_OPTIONS.map((option) => [option.toLowerCase(), option])
    );
    const seen = new Set();
    const selected = [];

    for (const item of normalizeToneList(tone)) {
        const match = byLower[item.toLowerCase()];
        if (!match || seen.has(match)) continue;
        seen.add(match);
        selected.push(match);
    }

    return selected.slice(0, MAX_TONES);
}

export function getFollowUpPaths(values) {
    const opportunities = values.opportunities ?? [];
    const hashtags = getEffectiveHashtags(values);
    const tones = normalizeToneList(values.tone);
    const topic = values.topic?.trim() ?? "";
    const target = values.target?.trim() ?? "";

    return {
        hasOpportunity: opportunities.length > 0,
        hasHashtag: hashtags.length > 0,
        hasAllT3: Boolean(tones.length > 0 && topic && target),
    };
}

export function isFollowUpStepValid(values) {
    return Object.keys(validateFollowUp(values)).length === 0;
}

export function validateFollowUp(values) {
    const errors = {};
    const opportunities = values.opportunities ?? [];
    const hashtags = values.hashtags ?? [];
    const tones = normalizeToneList(values.tone);
    const { hasOpportunity, hasAllT3 } = getFollowUpPaths(values);
    const topic = values.topic?.trim() ?? "";
    const target = values.target?.trim() ?? "";

    if (WIZARD_HASHTAGS_ENABLED && hashtags.length > MAX_FOLLOW_UP_HASHTAGS) {
        errors.hashtags = `You can select up to ${MAX_FOLLOW_UP_HASHTAGS} hashtags`;
    }

    if (opportunities.length > MAX_OPPORTUNITIES) {
        errors.opportunities = `You can select up to ${MAX_OPPORTUNITIES} trends`;
    }

    if (tones.length > MAX_TONES) {
        errors.tone = `You can select up to ${MAX_TONES} tones`;
    }

    if (hasOpportunity || hasAllT3) {
        return errors;
    }

    if (tones.length === 0) errors.tone = "Tone is required";
    if (!topic) errors.topic = "Topic is required";
    if (!target) errors.target = "Target is required";

    return errors;
}

/** Another path (T3) is satisfied — opportunity not required. */
export function isOpportunitySectionOptional(values) {
    const { hasAllT3 } = getFollowUpPaths(values);
    return hasAllT3;
}

/** Another path (opportunity or T3) is satisfied — hashtags not required. */
export function isHashtagSectionOptional(values) {
    const { hasOpportunity, hasAllT3 } = getFollowUpPaths(values);
    return hasOpportunity || hasAllT3;
}

/** Another path (opportunity) is satisfied — T3 fields not required. */
export function isT3DetailsOptional(values) {
    const { hasOpportunity } = getFollowUpPaths(values);
    return hasOpportunity;
}
